import axios, { AxiosInstance, AxiosError } from 'axios';
import { useAuthStore } from '@/store';
import type { ApiResponse, AuthResponse, User, DouyinVideo, DouyinComment, VideoCategory, HotVideoSearchResult, CommentSentimentAnalysis, UsageStatistics, UsageRecord } from '@/types';

// 创建 axios 实例
const apiClient: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 600000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器 - 添加 token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器 - 处理错误
apiClient.interceptors.response.use(
  (response) => response.data,
  (error: AxiosError<ApiResponse>) => {
    if (error.response?.status === 401) {
      // Token 过期，同步清除 Zustand 状态 + localStorage，再跳转登录页
      try {
        useAuthStore.getState().clearAuth();
      } catch (e) {
        // 如果 store 还未初始化，直接清 localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
      sessionStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);
      window.location.href = '/login';
      return Promise.reject(new Error('登录已过期，请重新登录'));
    }
    const data = error.response?.data;
    const msg = data?.error || data?.message || error.message;
    return Promise.reject(new Error(msg));
  }
);

// ==================== 认证 API ====================

export const authAPI = {
  register: (username: string, email: string, password: string): Promise<ApiResponse<AuthResponse>> =>
    apiClient.post('/auth/register', { username, email, password }),

  login: (username: string, password: string): Promise<ApiResponse<AuthResponse>> =>
    apiClient.post('/auth/login', { username, password }),

  openidLogin: (openId: string): Promise<ApiResponse<AuthResponse>> => {
    console.log('[DEBUG] authAPI.openidLogin 调用, openId:', openId ? openId.substring(0, 10) + '...' : '空');
    return apiClient.post('/auth/openid-login', { openId });
  },

  getMe: (): Promise<ApiResponse<User>> => apiClient.get('/auth/me'),

  refreshToken: (token: string): Promise<ApiResponse<AuthResponse>> =>
    apiClient.post('/auth/refresh', { token }),
};

// ==================== 视频 API ====================

export const videoAPI = {
  parseUrl: (url: string): Promise<ApiResponse<{ aweme_id: string; video_info: DouyinVideo }>> =>
    apiClient.post('/video/parse', { url }),

  parseProfile: (url: string): Promise<ApiResponse<{
    taskId: string;
  }>> => apiClient.post('/video/parse-profile', { url }),

  getVideoInfo: (awemeId: string): Promise<ApiResponse<DouyinVideo>> =>
    apiClient.get(`/video/${awemeId}/info`),

  getHotComments: (awemeId: string, count?: number): Promise<ApiResponse<{ comments: DouyinComment[]; count: number }>> =>
    apiClient.get(`/video/${awemeId}/hot-comments`, { params: { count } }),

  searchComments: (awemeId: string, keyword: string, max?: number): Promise<ApiResponse<{ comments: DouyinComment[]; total: number }>> =>
    apiClient.get(`/video/${awemeId}/search-comments`, { params: { keyword, max } }),

  // 【获取同类】根据profile搜索同类用户并保存（异步模式）
  findSimilarUsers: (recordId: string, profileData: any, requirements?: string): Promise<ApiResponse<{
    taskId: string;
  }>> => apiClient.post('/video/find-similar-users', { recordId, profileData, requirements }),

  // 查询异步任务状态
  getTaskStatus: (taskId: string): Promise<ApiResponse<{
    id: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress?: number;
    message?: string;
    result?: {
      keywords?: string[];
      users?: any[];
      count?: number;
      hotVideos?: any[];
      awemeIds?: string[];
      leads?: any[];
      summary?: string;
      processedCount?: number;
      skippedCount?: number;
      analysis?: any;
      detailedAnalysis?: any[];
      strategy?: string;
      [key: string]: any;
    };
    error?: string;
  }>> => apiClient.get(`/video/task/${taskId}`),

  cancelTask: (taskId: string): Promise<ApiResponse> =>
    apiClient.post(`/video/task/${taskId}/cancel`),

  // 【同类热门】获取已保存同类用户的近期热门视频（异步模式）
  // recordId: 可选，指定视频解析记录的ID；不传则获取所有同类用户的热门视频
  getSimilarUsersHot: (recordId?: string, count?: number): Promise<ApiResponse<{
    taskId: string;
  }>> => apiClient.get('/video/similar-users-hot', { params: recordId ? { recordId, count } : { count } }),

  // 【评论提炼】提炼指定视频的潜在客户评论（异步模式）
  extractLeads: (url: string, awemeId?: string, count?: number, page?: number, criteria?: string): Promise<ApiResponse<{
    taskId: string;
  }>> => apiClient.post('/video/extract-leads', { url, awemeId, count, page, criteria }),

  // 【评论分析】分析潜在客户意向并给出回复建议（异步模式）
  analyzeLeads: (leads: any[], videoContext?: any): Promise<ApiResponse<{
    taskId: string;
  }>> => apiClient.post('/video/analyze-leads', { leads, videoContext }),

  // 【单条评论分析】分析指定评论及其回复，给出深度分析和AI回复建议（异步模式）
  analyzeComment: (commentId: string, awemeId?: string, url?: string): Promise<ApiResponse<{
    taskId: string;
  }>> => apiClient.post('/video/analyze-comment', { commentId, awemeId, url }),

  // 获取当前用户保存的同类用户列表
  getMySimilarUsers: (limit?: number): Promise<ApiResponse<{
    users: any[];
    count: number;
  }>> => apiClient.get('/video/my-similar-users', { params: { limit } }),

  // 获取当前用户保存的同类热门视频
  getSavedHotVideos: (limit?: number): Promise<ApiResponse<{
    videos: any[];
    count: number;
  }>> => apiClient.get('/video/saved-hot-videos', { params: { limit } }),

  // 获取当前用户保存的评论提炼记录
  getSavedLeads: (limit?: number): Promise<ApiResponse<{
    leads: any[];
    count: number;
  }>> => apiClient.get('/video/saved-leads', { params: { limit } }),

  // 获取当前用户保存的评论分析记录
  getSavedAnalyses: (limit?: number): Promise<ApiResponse<{
    analyses: any[];
    count: number;
  }>> => apiClient.get('/video/saved-analyses', { params: { limit } }),

  // 批量评论提炼（支持多个视频）
  batchExtractLeads: (videos: { url?: string; awemeId?: string }[], count?: number): Promise<ApiResponse<{
    taskId: string;
  }>> => apiClient.post('/video/extract-leads', { videos, count }),

  replyComment: (awemeId: string, commentId: string, content: string): Promise<ApiResponse<{ comment_id: string; create_time: number }>> =>
    apiClient.post(`/video/${awemeId}/reply`, { comment_id: commentId, content }),

  batchReply: (awemeId: string, replies: { comment_id: string; content: string }[]): Promise<ApiResponse<{
    success: { comment_id: string; reply_id: string }[];
    failed: { comment_id: string; error: string }[];
  }>> => apiClient.post(`/video/${awemeId}/batch-reply`, { replies }),
};

// ==================== 发现/探索 API ====================

export const discoveryAPI = {
  getCategories: (): Promise<ApiResponse<VideoCategory[]>> =>
    apiClient.get('/discovery/categories'),

  getHotByCategory: (category: string, count?: number): Promise<ApiResponse<HotVideoSearchResult>> =>
    apiClient.get('/discovery/hot-by-category', { params: { category, count } }),

  searchVideos: (params: {
    keyword?: string;
    category?: string;
    count?: number;
    sort_by?: 'hot' | 'time' | 'play' | 'like';
  }): Promise<ApiResponse<HotVideoSearchResult>> => apiClient.get('/discovery/search-videos', { params }),

  sentimentAnalysis: (videoIds: string[], keyword?: string): Promise<ApiResponse<CommentSentimentAnalysis>> =>
    apiClient.post('/discovery/sentiment-analysis', { video_ids: videoIds, keyword }),

  quickSentiment: (keyword?: string, category?: string, videoCount?: number): Promise<ApiResponse<{
    search_info: HotVideoSearchResult;
    analysis: CommentSentimentAnalysis;
  }>> => apiClient.get('/discovery/quick-sentiment', { params: { keyword, category, video_count: videoCount } }),
};

// ==================== 聊天 API ====================

export const chatAPI = {
  sendMessage: (message: string, context?: any): Promise<ApiResponse<{
    message: string;
    type: string;
    data?: any;
  }>> => apiClient.post('/chat', { message, context }),
};

// ==================== 使用记录 API ====================

export const usageAPI = {
  getStatistics: (): Promise<ApiResponse<UsageStatistics>> =>
    apiClient.get('/usage/statistics'),

  getRecords: (params?: {
    page?: number;
    limit?: number;
    operation?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<UsageRecord[]>> => apiClient.get('/usage/records', { params }),

  getQuota: (): Promise<ApiResponse<{
    quota_total: number;
    quota_used: number;
    quota_remaining: number;
    reset_date: string;
  }>> => apiClient.get('/usage/quota'),
};

export default apiClient;
